import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils/cn";

import {
  downloadExpertXlsx,
  useImportConfirm,
  useImportPreview,
} from "../../api/hooks";
import type { ExpertImportReport, ImportError, IssueImportReport } from "../../types";

type Kind = "experts" | "issues";
type Report = ExpertImportReport | IssueImportReport;
type Row = { line_no: number; email: string; status: string; issue_id?: string };

function isExpertReport(r: Report): r is ExpertImportReport {
  return "created" in r;
}

/**
 * xlsx import (experts or issues): pick a file → dry-run preview → confirm.
 * The stat tiles are clickable, each revealing its rows below (accordion,
 * mirroring the legacy preview); nothing is written until confirm.
 */
export function AdminImport({ kind }: { kind: Kind }) {
  const { t } = useTranslation();
  const preview = useImportPreview(kind);
  const confirm = useImportConfirm(kind);
  const [report, setReport] = useState<Report | null>(null);
  const [done, setDone] = useState(false);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const onFile = (file: File | undefined) => {
    if (!file) return;
    setDone(false);
    setOpenKey("errors");
    preview.mutate(file, { onSuccess: (data) => setReport(data as Report) });
  };

  const onConfirm = () => {
    if (!report?.token) return;
    confirm.mutate(report.token, {
      onSuccess: (data) => {
        setReport(data as Report);
        setDone(true);
        setOpenKey(null);
      },
    });
  };

  // Build the tile set (label + value + optional row subset) for the report.
  const rows = (report?.rows ?? []) as Row[];
  const tiles: {
    key: string;
    label: string;
    value: number;
    rows?: Row[];
    isError?: boolean;
    isNote?: boolean;
  }[] = [];
  if (report) {
    tiles.push({
      key: "total",
      label: t("expert.admin.import.totalRows"),
      value: report.total_rows,
      rows,
    });
    if (isExpertReport(report)) {
      tiles.push({
        key: "created",
        label: t("expert.admin.import.created"),
        value: report.created,
        rows: rows.filter((r) => r.status === "created"),
      });
      tiles.push({
        key: "updated",
        label: t("expert.admin.import.updated"),
        value: report.updated,
        rows: rows.filter((r) => r.status === "updated"),
      });
      tiles.push({
        key: "skipped",
        label: t("expert.admin.import.skipped"),
        value: report.skipped,
        rows: rows.filter((r) => r.status.startsWith("skipped")),
      });
    } else {
      tiles.push({
        key: "matched",
        label: t("expert.admin.import.expertsMatched"),
        value: report.experts_matched,
      });
      tiles.push({
        key: "applied",
        label: t("expert.admin.import.pairsApplied"),
        value: report.pairs_applied,
        rows: rows.filter((r) => r.status === "applied"),
      });
      // Applied and kept, but hidden from the expert's form — stated so it
      // isn't mistaken for a failed pre-approval.
      tiles.push({
        key: "warnings",
        label: t("expert.admin.import.notOffered"),
        value: report.warnings.length,
        isNote: true,
      });
    }
    tiles.push({
      key: "errors",
      label: t("expert.admin.import.errors"),
      value: report.errors.length,
      isError: true,
    });
  }

  const errorsUrl = report?.token
    ? `/api/v1/experts/admin/import/${kind}/?errors=${report.token}`
    : "";

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">
          {kind === "experts"
            ? t("expert.admin.importExperts")
            : t("expert.admin.importIssues")}
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            void downloadExpertXlsx(
              `/api/v1/experts/admin/import/${kind}/?template=1`,
              `modele_${kind}.xlsx`,
            )
          }
        >
          {t("expert.admin.import.downloadTemplate")}
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="block text-sm">
          <span className="mb-1.5 block font-medium">
            {t("expert.admin.import.pick")}
          </span>
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => onFile(e.target.files?.[0] ?? undefined)}
            className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-sm file:font-medium"
          />
        </label>

        {report && (
          <div className="space-y-3">
            <p className="text-sm font-medium">
              {done
                ? t("expert.admin.import.applied")
                : t("expert.admin.import.preview")}
            </p>

            <div className="flex flex-wrap gap-3">
              {tiles.map((tile) => {
                const clickable =
                  tile.isError || tile.isNote
                    ? tile.value > 0
                    : (tile.rows?.length ?? 0) > 0;
                const active = openKey === tile.key;
                return (
                  <button
                    key={tile.key}
                    type="button"
                    disabled={!clickable}
                    onClick={() => setOpenKey(active ? null : tile.key)}
                    className={cn(
                      "relative rounded-md border px-3 py-2 text-center",
                      tile.isError && tile.value > 0 && "border-destructive/40",
                      tile.isNote && tile.value > 0 && "border-info/50",
                      !((tile.isError || tile.isNote) && tile.value > 0) &&
                        "border-border",
                      clickable && "cursor-pointer hover:bg-secondary/50",
                      active && "ring-2 ring-primary/40",
                      !clickable && "opacity-90",
                    )}
                  >
                    {clickable && (
                      <ChevronDown
                        className={cn(
                          "absolute right-1.5 top-1.5 size-3.5 text-muted-foreground transition-transform",
                          active && "rotate-180",
                        )}
                        aria-hidden
                      />
                    )}
                    <p
                      className={cn(
                        "text-xl font-bold tabular-nums",
                        tile.isError && tile.value > 0 && "text-destructive",
                        tile.isNote && tile.value > 0 && "text-info",
                      )}
                    >
                      {tile.value}
                    </p>
                    <p className="text-xs text-muted-foreground">{tile.label}</p>
                  </button>
                );
              })}
            </div>

            {report.duplicates > 0 && (
              <p className="text-xs text-muted-foreground">
                {t("expert.admin.import.duplicates", { count: report.duplicates })}
              </p>
            )}

            {/* expanded panel */}
            {openKey === "errors" && report.errors.length > 0 && (
              <div className="overflow-hidden rounded-md border border-destructive/40">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-destructive/[0.06] px-3 py-2">
                  <span className="text-sm font-semibold text-destructive">
                    {t("expert.admin.import.errorsHeading", {
                      count: report.errors.length,
                    })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() =>
                      void downloadExpertXlsx(errorsUrl, `erreurs_${kind}.xlsx`)
                    }
                  >
                    {t("expert.admin.import.downloadErrors")}
                  </Button>
                </div>
                <ErrorRows errors={report.errors} />
              </div>
            )}
            {openKey === "warnings" &&
              !isExpertReport(report) &&
              report.warnings.length > 0 && (
                <div className="overflow-hidden rounded-md border border-info/50">
                  <div className="border-b border-border bg-info/[0.08] px-3 py-2">
                    <span className="text-sm font-semibold text-info">
                      {t("expert.admin.import.notOfferedHeading", {
                        count: report.warnings.length,
                      })}
                    </span>
                  </div>
                  <ErrorRows errors={report.warnings} tone="info" />
                </div>
              )}
            {openKey && openKey !== "errors" && openKey !== "warnings" && (
              <RowTable
                rows={tiles.find((x) => x.key === openKey)?.rows ?? []}
                withIssue={!isExpertReport(report)}
              />
            )}

            {!done && report.token && (
              <div className="flex items-center justify-between pt-1">
                <span className="text-xs text-muted-foreground">
                  {t("expert.admin.import.chooseAnother")}
                </span>
                <Button onClick={onConfirm} disabled={confirm.isPending}>
                  {t("expert.admin.import.confirm")}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RowTable({ rows, withIssue }: { rows: Row[]; withIssue: boolean }) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;
  return (
    <div className="max-h-72 overflow-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-secondary/60 text-left">
          <tr>
            <th className="px-3 py-1.5">{t("expert.admin.import.rowLabel")}</th>
            <th className="px-3 py-1.5">{t("expert.admin.columns.email")}</th>
            {withIssue && <th className="px-3 py-1.5">issue_id</th>}
            <th className="px-3 py-1.5">{t("expert.admin.columns.status")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.line_no} className="border-t">
              <td className="px-3 py-1.5 tabular-nums text-muted-foreground">{r.line_no}</td>
              <td className="px-3 py-1.5">{r.email || "—"}</td>
              {withIssue && <td className="px-3 py-1.5">{r.issue_id || "—"}</td>}
              <td className="px-3 py-1.5">{r.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ErrorRows({
  errors,
  tone = "destructive",
}: {
  errors: ImportError[];
  tone?: "destructive" | "info";
}) {
  const { t } = useTranslation();
  return (
    <div className="max-h-72 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-secondary/60 text-left">
          <tr>
            <th className="px-3 py-1.5">{t("expert.admin.import.rowLabel")}</th>
            <th className="px-3 py-1.5">{t("expert.admin.columns.email")}</th>
            <th className="px-3 py-1.5">
              {tone === "info"
                ? t("expert.admin.import.noteColumn")
                : t("expert.admin.import.errorColumn")}
            </th>
          </tr>
        </thead>
        <tbody>
          {errors.map((e, i) => (
            <tr key={i} className="border-t">
              <td className="px-3 py-1.5 tabular-nums text-muted-foreground">
                {e.line_no}
              </td>
              <td className="px-3 py-1.5">{e.label || "—"}</td>
              <td
                className={cn(
                  "px-3 py-1.5",
                  tone === "info" ? "text-info" : "text-destructive",
                )}
              >
                {e.message}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
